import { Attribute } from "../../lib/attributes";
import { RegisterWidget, Widget } from "../../lib/widget";
import template from './totals.html';
import { ColorInput, RangeInput, SelectInput } from "../../../views/attributeeditorview";
import { CalculateInterval, HistoricalData } from "../../../cruncher";
import { Chart } from "chart.js";
import { TagAttribute, type TagDefinition } from "../../lib/tag";
import Cruncher from '../../../cruncher';
import { RateMap, UnitsMap } from "../../lib/tagunits";
import Icon from '../../../images/icons/bar-chart.svg';

@RegisterWidget({tag:'totals-chart', displayName: 'Totals Chart', icon: Icon,  section: 'Charts', editor: true, template: template})
export class TotalsChartWidget extends Widget {
    canvas: HTMLCanvasElement;
    tickInterval: ReturnType<typeof setInterval>;
    cruncher: Cruncher = new Cruncher();
    start: Date;
    end: Date;
    @Attribute({
        displayName: 'Color',
        getInput: (name, parent, property, getValue, onSettingChangedCallback, tooltip) => new ColorInput(name, parent, property, getValue, onSettingChangedCallback)
    }) color: string = '#000000';

    @Attribute({
        displayName: 'Period',
        getInput: (name, parent, property, getValue, onSettingChangedCallback, tooltip) => new SelectInput(name, parent, property, getValue, ['Hour','Day','Week','Year'], ['3600','86400','604800','31540000'], onSettingChangedCallback)
    }) period: number = 86400;

    @Attribute({displayName: 'Periods'}) periods: number = 7;

    @TagAttribute({
        displayName: 'Data Tag',
        requiredProperties: ['logged'],
        supportedTypes: ['numeric'],
        supportedUnits: ['flow-rate']
    }) dataTag: TagDefinition;

    protected connectedCallback(): void {
        this.canvas = this.shadowRoot?.getElementById('canvas') as HTMLCanvasElement;
    }

    protected enliven(): void {
        let period      = this.period;
        let interval    = CalculateInterval(period * this.periods, 10000)
        this.end        = new Date(Math.floor((new Date().getTime() + new Date().getTimezoneOffset()) / 1000 / period) * period * 1000);
        this.start      = new Date((this.end.getTime() / 1000 - this.periods * period) * 1000);
        this.cruncher.requestData(this.start, this.end, [this.dataTag.tag], interval).then(([tags, data]) => this.createTotalsGraph(data, interval))
    }

    createTotalsGraph(data: HistoricalData, interval: number) {
        let period  = this.period;
        let periods = this.periods;
        var fluxSets: any = [];
        let accumulatedUnits = RateMap.get(this.dataTag.tag.units)!.unit;

        let dayTotals: number[] = [];
        for (let i = 0; i < periods; ++i) {
            let cumulative  = 0;
            let index       = 0; // keep track of this for speediness
            let dateColumn  = data[1];
            let startTime = this.start.getTime() + i * period * 1000;
            let indexes = new Array(data[0]!.length).fill(0);		// Indexes of how far we've iterated through each data set
            for (let time = startTime; time < startTime + period * 1000; time += interval * 1000) {	// Go through the entire interval they requested at the interval they requested
                let value: number | null = this.cruncher.getValueAtTime(time, index, data, indexes);
                if (value == null)
                    continue; //TODO: add null range for bad data
                cumulative += value;
            }
            dayTotals.push(cumulative);
        }
        this.canvas.height = this.clientHeight;
        this.canvas.width = this.clientWidth;
        let ctx = this.canvas.getContext('2d')!;
        let dataLabels: number[] = [];
        for (let i=0;i<periods;++i)
            dataLabels.push(this.start.getTime() + i * period * 1000)
        let dataSets: any[] = [];
        dataSets.push( {
            label: `Total (${UnitsMap.get(accumulatedUnits)?.plural})`,
            backgroundColor: this.color,
            strokeColor: this.color,
            data: dayTotals
        })

        new Chart(ctx, {
            type: 'bar',
            data: {
                labels: dataLabels,
                datasets: dataSets
            },
            options: {
                scales: {
                x: {
                    stacked: true,
                    offset: true,
                    type: 'time',
                    time: {
                    parser: 'MM/DD/YYYY',
                    tooltipFormat: 'll',
                    unit: 'day',
                    displayFormats: {
                        'day': 'MM/DD/YYYY'
                    }
                    }
                },
                y: {
                    title: {
                        display: true,
                        text: UnitsMap.get(accumulatedUnits)?.plural
                    },
                    stacked: true,
                    ticks: {
                        sampleSize: period / interval,
                    },
                    type: 'linear',
                }
                },
                responsive: true,
                maintainAspectRatio: false,
                animation: {
                    duration: 0
                }
            }
        })
    }
}